import React from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Star, MapPin, Users, Award, Clock, CheckCircle } from 'lucide-react';

const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-20 w-72 h-72 bg-sky-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-violet-200 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-24 pb-16">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 bg-white shadow-soft border border-gray-100 rounded-full px-4 py-2 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
{/*             <div className="flex items-center gap-1"> */}
{/*               {[...Array(5)].map((_, i) => ( */}
{/*                 <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" /> */}
{/*               ))} */}
{/*             </div> */}
{/*             <span className="text-sm text-slate-600 font-medium">5.0 Google Rated</span> */}
{/*             <span className="text-slate-300">|</span> */}
            <span className="flex items-center gap-1 text-sm text-slate-600">
              <MapPin className="w-3 h-3 text-sky-500" />
              Ahmedabad, Gujarat
            </span>
          </motion.div>

          {/* H1 - SEO Optimized */}
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-['Oswald'] font-bold text-slate-900 leading-[1.1] mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="block">LED Sign Board</span>
            <span className="block mt-2">
              <span className="gradient-text">Manufacturer</span>
              <span className="text-slate-900"> in Ahmedabad</span>
            </span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto mb-10 font-['Manrope']"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Premium Signage Solutions in Gujarat — Custom LED sign boards, acrylic signage, 
            retail branding, petrol pump signage & corporate signage solutions.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <button
              onClick={scrollToContact}
              className="btn-primary group flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="get-free-quote-btn"
            >
              Get Free Quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="tel:+919825218789"
              className="btn-secondary group flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="call-now-btn"
            >
              <Phone className="w-5 h-5 text-sky-500" />
              Call Now
            </a>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {[
              { number: '500+', label: 'Projects Delivered', icon: CheckCircle },
              { number: '15+', label: 'Years Experience', icon: Award },
              { number: '200+', label: 'Happy Clients', icon: Users },
              { number: '24/7', label: 'Customer Support', icon: Clock },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-soft border border-gray-100">
                  <Icon className="w-6 h-6 text-sky-500 mx-auto mb-2" />
                  <div className="stat-number text-3xl md:text-4xl font-['Space_Grotesk'] font-bold">
                    {stat.number}
                  </div>
                  <div className="text-xs md:text-sm text-slate-500 mt-1 font-['Manrope']">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-slate-400 uppercase tracking-widest">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-sky-500 to-transparent"></div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
